// Document Builder Service - Database & Firebase Storage Integration
import { api } from './api';

class DocumentBuilderService {
    // Base URL for document API endpoints
    baseURL = '/api/documents';

    // ==================== DOCUMENT TEMPLATES ====================

    /**
     * Fetch all document templates from database
     */
    async getDocumentTemplates() {
        try {
            const response = await api.get(`${this.baseURL}/templates`);
            return response.data.templates || [];
        } catch (error) {
            console.error('Error fetching document templates:', error);
            throw error;
        }
    }

    /**
     * Save selected document templates
     */
    async saveSelectedTemplates(templates) {
        try {
            const response = await api.post(`${this.baseURL}/templates/selected`, {
                templates
            });
            return response.data;
        } catch (error) {
            console.error('Error saving selected templates:', error);
            throw error;
        }
    }

    /**
     * Get selected templates for current user/company
     */
    async getSelectedTemplates() {
        try {
            const response = await api.get(`${this.baseURL}/templates/selected`);
            return response.data.templates || {};
        } catch (error) {
            console.error('Error fetching selected templates:', error);
            throw error;
        }
    }

    // ==================== DOCUMENT GENERATION ====================

    /**
     * Generate a new document from template
     */
    async generateDocument(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/generate`, {
                category: documentData.category,
                templateId: documentData.templateId,
                templateName: documentData.templateName,
                content: documentData.content,
                isDraft: documentData.isDraft || false
            });
            return response.data.document;
        } catch (error) {
            console.error('Error generating document:', error);
            throw error;
        }
    }

    /**
     * Update existing document
     */
    async updateDocument(documentId, updates) {
        try {
            const response = await api.put(`${this.baseURL}/${documentId}`, updates);
            return response.data.document;
        } catch (error) {
            console.error('Error updating document:', error);
            throw error;
        }
    }

    /**
     * Delete document
     */
    async deleteDocument(documentId) {
        try {
            const response = await api.delete(`${this.baseURL}/${documentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    }

    // ==================== DOCUMENT DRAFTS ====================

    /**
     * Save document as draft
     */
    async saveDraft(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/drafts`, {
                ...documentData,
                isDraft: true
            });
            return response.data.draft;
        } catch (error) {
            console.error('Error saving draft:', error);
            throw error;
        }
    }

    /**
     * Get all drafts
     */
    async getDrafts() {
        try {
            const response = await api.get(`${this.baseURL}/drafts`);
            return response.data.drafts || [];
        } catch (error) {
            console.error('Error fetching drafts:', error);
            return [];
        }
    }

    /**
     * Delete draft
     */
    async deleteDraft(draftId) {
        try {
            const response = await api.delete(`${this.baseURL}/drafts/${draftId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting draft:', error);
            throw error;
        }
    }

    // ==================== DOCUMENT DISTRIBUTION ====================

    /**
     * Send document to employee(s)
     */
    async sendDocument(documentId, recipientData) {
        try {
            const response = await api.post(`${this.baseURL}/${documentId}/send`, {
                employeeId: recipientData.employeeId,
                employeeEmail: recipientData.employeeEmail,
                sendViaEmail: recipientData.sendViaEmail || false,
                sendViaPortal: recipientData.sendViaPortal || true,
                metadata: {
                    sentBy: recipientData.sentBy || 'HR Department',
                    sentAt: new Date().toISOString(),
                    department: recipientData.department
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error sending document:', error);
            throw error;
        }
    }

    /**
     * Send document to multiple employees
     */
    async sendDocumentBulk(documentId, recipients) {
        try {
            const response = await api.post(`${this.baseURL}/${documentId}/send-bulk`, {
                recipients,
                sentBy: 'HR Department',
                sentAt: new Date().toISOString()
            });
            return response.data;
        } catch (error) {
            console.error('Error sending document to multiple recipients:', error);
            throw error;
        }
    }

    /**
     * Track document send status
     */
    async getDocumentStatus(documentId) {
        try {
            const response = await api.get(`${this.baseURL}/${documentId}/status`);
            return response.data.status;
        } catch (error) {
            console.error('Error fetching document status:', error);
            throw error;
        }
    }

    // ==================== DOCUMENT RETRIEVAL ====================

    /**
     * Get all generated documents (admin view)
     */
    async getGeneratedDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/generated`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error fetching generated documents:', error);
            return [];
        }
    }

    /**
     * Get documents for specific employee
     */
    async getEmployeeDocuments(employeeId) {
        try {
            const response = await api.get(`${this.baseURL}/employee/${employeeId}`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error fetching employee documents:', error);
            return [];
        }
    }

    /**
     * Get company documents (visible to all employees)
     */
    async getCompanyDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/company`);
            return response.data.documents || [];
        } catch (error) {
            console.error('Error fetching company documents:', error);
            return [];
        }
    }

    /**
     * Get my documents (for logged-in employee)
     */
    async getMyDocuments() {
        try {
            const response = await api.get(`${this.baseURL}/my-documents`);
            return {
                received: response.data.received || [],
                uploaded: response.data.uploaded || [],
                company: response.data.company || []
            };
        } catch (error) {
            console.error('Error fetching my documents:', error);
            return { received: [], uploaded: [], company: [] };
        }
    }

    // ==================== FIREBASE STORAGE ====================

    /**
     * Upload document to Firebase Storage
     */
    async uploadToFirebase(documentFile, metadata = {}) {
        try {
            const formData = new FormData();
            formData.append('document', documentFile);
            formData.append('metadata', JSON.stringify(metadata));

            const response = await api.post(`${this.baseURL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return {
                url: response.data.url,
                path: response.data.path,
                documentId: response.data.documentId
            };
        } catch (error) {
            console.error('Error uploading to Firebase:', error);
            throw error;
        }
    }

    /**
     * Generate PDF from document content and upload to Firebase
     */
    async generateAndUploadPDF(documentData) {
        try {
            const response = await api.post(`${this.baseURL}/generate-pdf`, {
                content: documentData.content,
                templateName: documentData.templateName,
                employeeName: documentData.employeeName,
                metadata: documentData.metadata
            });

            return {
                pdfUrl: response.data.pdfUrl,
                firebasePath: response.data.firebasePath,
                documentId: response.data.documentId
            };
        } catch (error) {
            console.error('Error generating and uploading PDF:', error);
            throw error;
        }
    }

    /**
     * Get document download URL from Firebase
     */
    async getDocumentUrl(documentId) {
        try {
            const response = await api.get(`${this.baseURL}/${documentId}/url`);
            return response.data.url;
        } catch (error) {
            console.error('Error getting document URL:', error);
            throw error;
        }
    }

    /**
     * Delete document from Firebase Storage
     */
    async deleteFromFirebase(documentPath) {
        try {
            const response = await api.delete(`${this.baseURL}/storage`, {
                data: { path: documentPath }
            });
            return response.data;
        } catch (error) {
            console.error('Error deleting from Firebase:', error);
            throw error;
        }
    }

    // ==================== SEARCH & FILTERS ====================

    /**
     * Search documents
     */
    async searchDocuments(query, filters = {}) {
        try {
            const response = await api.post(`${this.baseURL}/search`, {
                query,
                ...filters
            });
            return response.data.documents || [];
        } catch (error) {
            console.error('Error searching documents:', error);
            return [];
        }
    }

    // ==================== STATISTICS ====================

    /**
     * Get document statistics
     */
    async getDocumentStats() {
        try {
            const response = await api.get(`${this.baseURL}/stats`);
            return response.data;
        } catch (error) {
            console.error('Error fetching document stats:', error);
            return {};
        }
    }
}

export default new DocumentBuilderService();